import { memo, useState } from "react";
import { X } from "lucide-react";
import type { BotezBokehTemplateProps } from "./types";
import { CORAL, CORAL_TEXT_LARGE, FONT_SCRIPT, PEACHY, TEAL } from "./utils";

// Playful rotation angles for the polaroid cards (repeats past 6 images)
const ROTATIONS = [2, -3, 4, -2, 3, -1];

export const BotezBokehGallery = memo(({ event, config, t }: BotezBokehTemplateProps) => {
  const [selectedImage, setSelectedImage] = useState<number | null>(null);
  const gallery = event.gallery ?? [];

  if (gallery.length === 0 || !(event.showGallery ?? true)) return null;

  return (
    <section id="gallery" className="relative z-10 py-16" style={{ background: config.palette.surface }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h2
            className="text-4xl sm:text-5xl mb-4"
            style={{ fontFamily: FONT_SCRIPT, color: CORAL_TEXT_LARGE }}
          >
            {t("events.form.template.momentsWeTreasure")}
          </h2>
        </div>

        {/* Polaroid-style grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
          {gallery.map((src, index) => (
            <button
              type="button"
              key={index}
              className={`relative cursor-pointer group text-left motion-safe:transition-transform motion-safe:duration-300 motion-safe:hover:rotate-0 motion-safe:hover:scale-105 motion-safe:hover:z-10 ${
                index === 0 ? "md:col-span-2 md:row-span-2" : ""
              }`}
              style={{ transform: `rotate(${ROTATIONS[index % ROTATIONS.length]}deg)` }}
              onClick={() => setSelectedImage(index)}
            >
              <div className="bg-white p-3 pb-10 shadow-xl group-hover:shadow-2xl transition-shadow duration-300 rounded-sm">
                <div
                  className={`w-full ${index === 0 ? "aspect-[4/3]" : "aspect-square"} overflow-hidden relative`}
                >
                  <img
                    src={src}
                    alt={`${t("events.form.template.gallery")} ${index + 1}`}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover motion-safe:transition-transform motion-safe:duration-500 motion-safe:group-hover:scale-110"
                  />
                  <div
                    className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                    style={{ background: `linear-gradient(to top, ${TEAL}33, transparent)` }}
                  />
                </div>
              </div>

              {/* Decorative tape strip */}
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-6 rotate-2"
                aria-hidden="true"
                style={{
                  backgroundColor: `${CORAL}33`,
                  borderLeft: `2px dashed ${CORAL}`,
                  borderRight: `2px dashed ${CORAL}`,
                  opacity: 0.4,
                }}
              />

              {/* Playful blob accents */}
              {index % 3 === 0 && (
                <div
                  className="absolute -bottom-3 -right-3 w-10 h-10 rounded-full -z-10"
                  aria-hidden="true"
                  style={{ backgroundColor: `${CORAL}4D` }}
                />
              )}
              {index % 3 === 1 && (
                <div
                  className="absolute -top-3 -left-3 w-8 h-8 -z-10"
                  aria-hidden="true"
                  style={{
                    backgroundColor: `${TEAL}4D`,
                    borderRadius: "50% 40% 60% 40% / 60% 50% 40% 50%",
                    transform: "rotate(45deg)",
                  }}
                />
              )}
              {index % 3 === 2 && (
                <div
                  className="absolute -bottom-2 -left-2 w-6 h-6 -z-10"
                  aria-hidden="true"
                  style={{
                    backgroundColor: `${PEACHY}80`,
                    borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
                  }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div
          className="fixed inset-0 bg-black/90 z-[70] flex items-center justify-center p-4"
          onClick={() => setSelectedImage(null)}
        >
          <button
            type="button"
            className="absolute top-6 right-6 text-white hover:opacity-70 transition-opacity"
            aria-label={t("common.close")}
            onClick={() => setSelectedImage(null)}
          >
            <X className="w-8 h-8" aria-hidden="true" />
          </button>
          <img
            src={gallery[selectedImage]}
            alt={`${t("events.form.template.gallery")} ${selectedImage + 1}`}
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            decoding="async"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
});

BotezBokehGallery.displayName = "BotezBokehGallery";
